import type { CasAssignmentStatus, CasType } from '@mathflat/domain/Cas'
import type { AllGradeSubject, Revision, SchoolType, Semester } from '@mathflat/domain/SchoolSystem'
import {
  getKoreanElementaryMiddleGradeAbbr,
  SCHOOL_TYPE_KO_ABBR,
} from '@mathflat/domain/SchoolSystem'
import type { RecentStudyItem, RecentStudyType } from '@mathflat/domain/StudentHome'
import type { LearningStatus } from '@mathflat/domain/Worksheet'
import { computed, makeObservable } from 'mobx'

import type { Worksheet } from '~/@common/types/Worksheet'

export default class RecentStudy {
  readonly type: RecentStudyType
  readonly examType: RecentStudyItem['examType']
  readonly id: number
  readonly datetime: string
  readonly status: LearningStatus
  readonly examScheduleProgress: RecentStudyItem['examScheduleProgress']
  private readonly _accessModifierToStudent: Worksheet['accessModifierToStudent']
  readonly isAutoScored: boolean
  readonly isEssay: boolean
  readonly essayScoringOpened: boolean
  readonly revision: Revision | null
  readonly school: SchoolType
  readonly grade: AllGradeSubject
  readonly semester: Semester | null
  readonly title: string
  readonly existsHomework: boolean | null
  readonly isSignatureWorkbook: boolean | null
  readonly problemsCount: number
  private readonly _score: number | null
  private readonly _workbookPage: string | null
  readonly workbookThumbnailImageUrl: string | null
  readonly studentWorksheetId: number | null
  readonly studentWorkbookId: number | null
  readonly studentWorkbookProgressId: number | null
  readonly studentWorkbookRevisionId: number | null
  readonly studentWorkbookProgressPageNumber: number | null
  private readonly _studentWorkbookRevisionRound: number | null
  readonly studentExamId: number | null
  readonly studentExamOpenDatetime: string | null
  // CAS 전용 (type === 'CAS' 일 때만 값이 있음)
  readonly assignedCasId: number | null
  readonly casId: number | null
  readonly casType: CasType | null
  readonly casRevision: Revision | null
  readonly assignedCasStatus: CasAssignmentStatus | null
  readonly casStartedAt: string | null
  readonly casCompletedAt: string | null

  constructor(data: {
    type: RecentStudyType
    examType: RecentStudyItem['examType']
    id: number
    datetime: string
    status: LearningStatus
    examScheduleProgress: RecentStudyItem['examScheduleProgress']
    accessModifierToStudent: Worksheet['accessModifierToStudent']
    isAutoScored: boolean
    isEssay?: boolean | null
    essayScoringOpened?: boolean | null
    title: string
    revision: Revision | null
    school: SchoolType
    grade: AllGradeSubject
    semester: Semester | null
    existsHomework: boolean | null
    isSignatureWorkbook: boolean | null
    problemsCount: number
    score: number | null
    workbookPage: string | null
    workbookThumbnailImageUrl: string | null
    studentWorksheetId: number | null
    studentWorkbookId: number | null
    studentWorkbookProgressId: number | null
    studentWorkbookRevisionId: number | null
    studentWorkbookProgressPageNumber: number | null
    studentWorkbookRevisionRound: number | null
    studentExamId: number | null
    studentExamOpenDatetime: string | null
    assignedCasId?: number | null
    casId?: number | null
    casType?: CasType | null
    casRevision?: Revision | null
    assignedCasStatus?: CasAssignmentStatus | null
    casStartedAt?: string | null
    casCompletedAt?: string | null
  }) {
    this.type = data.type
    this.examType = data.examType
    this.id = data.id
    this.datetime = data.datetime
    this.status = data.status
    this.examScheduleProgress = data.examScheduleProgress
    this._accessModifierToStudent = data.accessModifierToStudent
    this.isAutoScored = data.isAutoScored
    this.isEssay = data.isEssay ?? false
    this.essayScoringOpened = data.essayScoringOpened ?? false
    this.revision = data.revision
    this.school = data.school
    this.grade = data.grade
    this.semester = data.semester
    this.title = data.title
    this.existsHomework = data.existsHomework
    this.isSignatureWorkbook = data.isSignatureWorkbook
    this.problemsCount = data.problemsCount
    this._score = data.score
    this._workbookPage = data.workbookPage
    this.workbookThumbnailImageUrl = data.workbookThumbnailImageUrl
    this.studentWorksheetId = data.studentWorksheetId
    this.studentWorkbookId = data.studentWorkbookId
    this.studentWorkbookProgressId = data.studentWorkbookProgressId
    this.studentWorkbookRevisionId = data.studentWorkbookRevisionId
    this.studentWorkbookProgressPageNumber = data.studentWorkbookProgressPageNumber
    this._studentWorkbookRevisionRound = data.studentWorkbookRevisionRound
    this.studentExamId = data.studentExamId
    this.studentExamOpenDatetime = data.studentExamOpenDatetime
    this.assignedCasId = data.assignedCasId ?? null
    this.casId = data.casId ?? null
    this.casType = data.casType ?? null
    this.casRevision = data.casRevision ?? null
    this.assignedCasStatus = data.assignedCasStatus ?? null
    this.casStartedAt = data.casStartedAt ?? null
    this.casCompletedAt = data.casCompletedAt ?? null

    makeObservable<this>(this, {
      displayStatus: computed,
      studentWorkbookRevisionRound: computed,
      workbookPages: computed,
      workbookPagesText: computed,
      score: computed,
      isPrivate: computed,
    })
  }

  /**
   * 서술형은 채점 공개가 status 를 앞지른다 — 전 문항 채점 전이라 PROGRESS 로 내려와도
   * 공개된 순간 학생에게는 결과를 볼 수 있는 학습지다.
   * 단 숙제는 서술형 여부와 무관하게 다른 숙제와 같은 status 표기를 쓴다
   */
  get displayStatus(): LearningStatus {
    if (this.type === 'HOMEWORK') return this.status

    return this.isEssay && this.essayScoringOpened ? 'COMPLETE' : this.status
  }

  get schoolTypeGrade() {
    if (this.school === 'HIGH') {
      return getKoreanElementaryMiddleGradeAbbr(this.grade, this.school)
    }

    if (this.semester) {
      return `${SCHOOL_TYPE_KO_ABBR[this.school]}${this.grade}-${this.semester}`
    }

    return `${SCHOOL_TYPE_KO_ABBR[this.school]}${this.grade}`
  }

  get studentWorkbookRevisionRound() {
    if (this.type === 'WORKBOOK' && this._studentWorkbookRevisionRound) {
      return this._studentWorkbookRevisionRound
    }
  }

  getIsAccessible(): boolean {
    if (this.type === 'EXAM') {
      return !(this.status === 'INCOMPLETE' && this.examScheduleProgress !== 'IN_PROGRESS')
    }

    return true
  }

  get workbookPages() {
    if (this.type === 'WORKBOOK' && this.existsHomework && this._workbookPage) {
      return this._workbookPage.split(',').map((v) => {
        const pages = v.split('~')
        if (pages.length > 1) {
          return [Number(pages[0]), Number(pages[1])]
        }
        return Number(pages[0])
      })
    }
  }

  get workbookPagesText() {
    if (!this.workbookPages?.length) {
      return
    }
    const text = this.workbookPages
      .map((v) => {
        if (Array.isArray(v)) {
          return `p${v[0]} - p${v[1]}`
        }
        return `p${v}`
      })
      .join(', ')
    return text
  }

  get score() {
    if (this.type !== 'EXAM' && this.type !== 'WORKSHEET' && this.type !== 'CAS') return
    // 서술형 점수는 공개 전까지 감춘다 — 채점이 돌아 점수가 잡혀도 교사가 열기 전에는 비공개다
    if (this.isEssay) return this.essayScoringOpened ? this._score : undefined
    if (this.status === 'COMPLETE') {
      return this._score
    }
  }

  get isPrivate() {
    return this._accessModifierToStudent === 'PRIVATE'
  }
}
